import { UntypedFormBuilder, UntypedFormControl, Validators, UntypedFormGroup } from '@angular/forms';
import { Component, Input, OnInit, Output, EventEmitter, OnChanges } from '@angular/core';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AcOnlinetestModel, OnlinetestTypes } from 'projects/core/src/lib/models/ac-onlinetest.model';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { PartnerModel } from 'projects/core/src/lib/models/partner.model';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';
import { Subscription } from 'rxjs';
import { OnlinetestModel } from 'projects/core/src/lib/models/onlinetest.model';
import { TranslocoService } from '@ngneat/transloco';
import { ONLINETEST_TYPE_NAMES } from 'projects/core/src/lib/constants';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

@Component({
  selector: 'app-template-onlinetests',
  templateUrl: './template-onlinetests.component.html',
  styleUrls: ['./template-onlinetests.component.scss']
})
export class TemplateOnlinetestsComponent implements OnInit, OnChanges {

  @Input() template: TemplateModel = null;
  @Input('partners') all: Array<PartnerModel> = []; // tslint:disable-line:no-input-rename
  @Output() onlinetestsChange = new EventEmitter<TemplateModel>();
  @Output() setEdit = new EventEmitter<string>();

  private formTypes = {
    normal: this.fb.group({
      onlinetest: new UntypedFormControl(null),
      duration: new UntypedFormControl(null, [Validators.required]),
      rating_duration: new UntypedFormControl(null),
      login_url: new UntypedFormControl(null, [CustomValidators.url()]),
      login_user: new UntypedFormControl(null),
      login_password: new UntypedFormControl(null),
      comment: new UntypedFormControl(null),
    }),
    before: this.fb.group({
      onlinetest: new UntypedFormControl(null),
      unlock_days: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(60)]),
      login_url: new UntypedFormControl(null, [CustomValidators.url()]),
      login_user: new UntypedFormControl(null),
      login_password: new UntypedFormControl(null),
      comment: new UntypedFormControl(null),
    })
  };

  public inEdit = false;
  public editOnlinetest: AcOnlinetestModel = null;
  public editType: OnlinetestTypes;
  public typeNames: any = ONLINETEST_TYPE_NAMES;
  public submitting = false;
  public form: UntypedFormGroup = null;
  public optionsPartners: Array<any> = [];
  public editCompetences: Array<CompetenceModel> = [];
  public hiddenCompetences: Array<CompetenceModel> = [];
  public items: Array<MenuItem> = [
    {
      label: this.transloco.translate('common.labels.before'),
      icon: 'pi pi-calendar',
      command: () => {
        this.onNew(OnlinetestTypes.Before);
      }
    },
    {
      label: this.transloco.translate('template_onlinetests.during_ac'),
      icon: 'pi pi-sitemap',
      command: () => {
        this.onNew(OnlinetestTypes.Normal);
      }
    },
  ];
  private onlinetestSubscription: Subscription = null;
  public deleteInheritVisible = false;
  public deleteInheritOnlinetest: AcOnlinetestModel = null;
  public saveInheritVisible = false;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private auth: AuthStateService,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.ui.setFocus();
  }

  ngOnChanges(): void {
    this.buildOnlinetests();
  }

  onNew(type: OnlinetestTypes): void {
    if (this.auth.hasRight('admin.template.edit')) {
      this.editType = type;
      this.form = this.formTypes[this.editType];
      this.form.get('onlinetest').setValidators([Validators.required]);
      this.form.get('onlinetest').updateValueAndValidity();
      this.clearForm();
      this.editOnlinetest = null;
      this.editCompetences = [];
      this.hiddenCompetences = [];
      this.onlinetestSubscription = this.form.get('onlinetest').valueChanges.subscribe((value: OnlinetestModel) => {
        if (value !== null) {
          this.editCompetences = value.competences;
          this.hiddenCompetences = [];
        } else {
          this.editCompetences = [];
          this.hiddenCompetences = [];
        }
      });
      this.startEdit();
    }
  }

  onEdit(onlinetest: AcOnlinetestModel): void {
    if (this.auth.hasRight('admin.template.edit')) {
      this.editOnlinetest = onlinetest;
      this.editType = this.editOnlinetest.type;
      this.form = this.formTypes[this.editType];
      this.form.get('onlinetest').clearValidators();
      this.form.get('onlinetest').updateValueAndValidity();
      if (this.editType === 'before') {
        this.form.patchValue({
          unlock_days: onlinetest.unlock_days,
          login_url: onlinetest.pivot?.misc?.login_url,
          login_user: onlinetest.pivot?.misc?.login_user,
          login_password: onlinetest.pivot?.misc?.login_password,
          comment: onlinetest.pivot?.misc?.comment,
        });
      } else {
        this.form.patchValue({
          duration: onlinetest.duration,
          rating_duration: onlinetest.rating_duration,
          login_url: onlinetest.pivot?.misc?.login_url,
          login_user: onlinetest.pivot?.misc?.login_user,
          login_password: onlinetest.pivot?.misc?.login_password,
          comment: onlinetest.pivot?.misc?.comment,
        });
      }
      this.editCompetences = this.editOnlinetest.competences;
      this.hiddenCompetences = this.editOnlinetest.hidden_competences;
      this.startEdit();
    }
  }

  onDelete(onlinetest: AcOnlinetestModel): void {
    if (this.auth.hasRight('admin.template.edit')) {
      this.confirmation.confirm({
        message: this.transloco.translate('ac_onlinetests.really_delete', { value: onlinetest.fullname }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.deleteInheritOnlinetest = onlinetest;
          if (this.template?.shouldShowInheritDialog()) {
            this.deleteInheritVisible = true;
          } else {
            this.deleteOnlinetest(false);
          }
        }
      });
    }
  }

  onCancel(): void {
    this.stopEdit();
    this.ui.hideMessage();
  }

  onSubmit(): void {
    if (this.template !== null && this.inEdit === true) {
      this.ui.hideMessage();
      if (this.form.valid === true) {
        if (this.template?.shouldShowInheritDialog()) {
          this.saveInheritVisible = true
        } else {
          this.saveOnlinetest(false);
        }
      } else {
        this.ui.showMessage(
          'error',
          this.transloco.translate('common.error_on_form_please_correct'),
          this.transloco.translate('common.error')
        );
        FormHelper.markFormAsDirty(this.form);
      }
    }
  }

  private deleteOnlinetest(inherit: boolean = false): void {
    this.api.deleteAcOnlinetest('templates', this.template.id, this.deleteInheritOnlinetest.id, inherit).subscribe({
      next: () => {
        this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.deleteInheritOnlinetest.fullname }), this.transloco.translate('common.deleted'));
        const index = this.template.onlinetests.indexOf(this.deleteInheritOnlinetest);
        if (index > -1) {
          this.template.onlinetests.splice(index, 1);
        }
        this.buildOnlinetests();
        this.emitOnlintestsChanged();
      },
      error: (error: any) => {
        this.ui.toastError(this.transloco.translate(error.message));
      }
    });
  }

  private saveOnlinetest(inherit: boolean = false): void {
    this.submitting = true;
    const payload: any = {
      onlinetest: (this.editOnlinetest !== null) ? this.editOnlinetest.id : this.form.get('onlinetest').value.id,
      type: this.editType,
      hidden_competences: this.hiddenCompetences.map((competence: CompetenceModel) => {
        return competence.id;
      }),
      inherit
    };
    const misc = {
      login_url: this.form.get('login_url').value,
      login_user: this.form.get('login_user').value,
      login_password: this.form.get('login_password').value,
      comment: this.form.get('comment').value,
    };
    if (this.editType === OnlinetestTypes.Before) {
      payload.unlock_days = this.form.get('unlock_days').value;
      payload.misc = misc;
    } else {
      payload.duration =  this.form.get('duration').value;
      payload.rating_duration = this.form.get('rating_duration').value;
      payload.misc = misc;
    }
    this.api.saveAcOnlinetest('templates', this.template.id, payload).subscribe({
        next: (result: any) => {
          let name: string;
          if (this.editOnlinetest !== null) {
            this.editOnlinetest.deserialize(result.data);
            name = this.editOnlinetest.name;
          } else {
            const newOnlinetest = new AcOnlinetestModel().deserialize(result.data);
            this.template.onlinetests.push(newOnlinetest);
            name = newOnlinetest.name;
          }
          this.ui.toastSuccess(
            this.transloco.translate('common.messages.saved', { value: name }),
            this.transloco.translate('common.saved')
          );
          this.emitOnlintestsChanged();
          this.buildOnlinetests();
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });

  }

  startEdit(): void {
    this.inEdit = true;
    this.setEdit.emit('onlinetests');
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.editType = null;
    this.clearForm();
    this.setEdit.emit(null);
    this.ui.setFocus();
    if (this.onlinetestSubscription !== null) {
      this.onlinetestSubscription.unsubscribe();
    }
  }

  setHiddenCompetences(competences: Array<CompetenceModel>): void {
    this.hiddenCompetences = competences;
  }

  private buildOnlinetests(): void {
    this.optionsPartners = [];
    const templateIds = this.template.onlinetests.map((onlinetest: OnlinetestModel) => {
      return onlinetest.id;
    });
    this.all?.forEach((actualPartner: PartnerModel) => {
      const partner = new PartnerModel().deserialize({
        ...actualPartner,
        onlinetests: actualPartner.onlinetests.filter((onlinetest: OnlinetestModel) => {
          return onlinetest.for_ac === true && templateIds.indexOf(onlinetest.id) === -1;
        })
      });
      if (partner.onlinetests.length > 0) {
        this.optionsPartners.push(partner);
      }
    });
  }

  private emitOnlintestsChanged(): void {
    this.template = new TemplateModel().deserialize({...this.template});
    this.onlinetestsChange.emit(this.template);
  }

  private clearForm(): void {
    if (this.form !== null) {
      this.form.reset();
      if (this.editType === OnlinetestTypes.Before) {
        this.form.patchValue({
          unlock_days: null,
          login_url: null,
          login_user: null,
          login_password: null,
          comment: null,
        });
      } else {
        this.form.patchValue({
          duration: null,
          rating_duration: null,
          login_url: null,
          login_user: null,
          login_password: null,
          comment: null,
        });
      }
    }
  }

}
